import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as PriceListV2Actions from './priceListv2.actions';
import { Store } from '@ngrx/store';
import { catchError, concatMap, map, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { globalFailure } from './../../../app/@store/common.actions';
import * as _ from 'lodash';
import { PriceListV2 } from './../../../app/@shared/models/pricelist.model';
import { ContractManagementApiService } from './../../../app/@shared/services/contractmanagementapi.service';
import { getPriceListsMeta, getSelectedPriceListContractsMeta } from './priceListv2.selectors';

import { loginSuccess } from '../auth/auth.actions';
import { selectRouteParam } from '../router/router.selector';
import { contractmanagement_api } from '@gridscale/gs-services/contractmanagement_api';
@Injectable()
export class PriceListsV2Effects {
  constructor(private readonly actions$: Actions, private readonly store: Store, private readonly contractManagementApi: ContractManagementApiService) { }

  loadPriceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PriceListV2Actions.loadPriceList,
        PriceListV2Actions.patchPriceListSuccess,
        PriceListV2Actions.deletePriceListSuccess,
        PriceListV2Actions.createPriceListSuccess,
        //    PriceListV2Actions.patchPriceListSuccess,
        PriceListV2Actions.addContractsToPriceListSuccess,
        loginSuccess
      ),
      withLatestFrom(this.store.select(getPriceListsMeta)),
      concatMap(([action, config]) => {
        if (!config) {
          config = {
            page: '0',
            limit: '100',
            count: '0',
            total: '0',
          };
        }
        return this.contractManagementApi
          .get<contractmanagement_api.PriceListResponse>('/management/v2/pricelists', {
            'page[number]': (parseInt(config.page || '0', 10) * 1 + 1).toString(),
            'page[size]': String(config.limit),
            sort: '-is_default_price_list,-assigned_contracts_count,name'
          })
          .pipe(
            map(result => {
              return PriceListV2Actions.loadPriceListSuccess({
                result
              });
            }),
            catchError(error => of(PriceListV2Actions.loadPriceListFailure({ error }), globalFailure({ error })))
          );
      })
    )
  );

  loadPriceListNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.loadPriceListNext),
      withLatestFrom(this.store.select(getPriceListsMeta)),
      concatMap(([action, config]) => {
        return this.contractManagementApi
          .get<contractmanagement_api.PriceListResponse>('/management/v2/pricelists', {
            'page[number]': (parseInt(config?.page || '0', 10) * 1 + 1).toString(),
            'page[size]': String(config?.limit || 10),
            sort: '-is_default_price_list,-assigned_contracts_count,name'
          })
          .pipe(
            map(result => {
              return PriceListV2Actions.loadPriceListNextSuccess({
                result
              });
            }),
            catchError(error => of(PriceListV2Actions.loadPriceListNextFailure({ error }), globalFailure({ error })))
          );
      })
    )
  );

  loadDefaultPriceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.loadDefaultPriceList, loginSuccess),
      concatMap(action => {
        return this.contractManagementApi
          .get<contractmanagement_api.PriceListResponse>('/management/v2/pricelists', {
            'page[number]': '1',
            'page[size]': '1',
            sort: '-is_default_price_list'
          })
          .pipe(
            map(result => {
              return PriceListV2Actions.loadDefaultPriceListSuccess({
                result
              });
            }),
            catchError(error => of(PriceListV2Actions.loadDefaultPriceListFailure({ error }), globalFailure({ error })))
          );
      })
    )
  );

  getPriceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PriceListV2Actions.getPriceList,
        PriceListV2Actions.patchPriceListSuccess,
        PriceListV2Actions.patchPriceListPricesSuccess,
        PriceListV2Actions.addContractsToPriceListSuccess,
        PriceListV2Actions.createPriceListSuccess
      ),
      concatMap(
        action =>
          this.contractManagementApi.get<contractmanagement_api.PriceListDetailResponse>('/management/v2/pricelists/' + action.price_list_uuid, {}).pipe(
            map(result => {
              return PriceListV2Actions.getPriceListSuccess({
                price_list_uuid: action.price_list_uuid,
                result
              });
            }),
            catchError(error => of(PriceListV2Actions.getPriceListFailure({ error, price_list_uuid: action.price_list_uuid }), globalFailure({ error })))
          )
      )
    )
  );

  getCurrentPriceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.getCurrentPriceList),
      withLatestFrom(this.store.select(selectRouteParam('price_list_uuid'))),
      concatMap(([action, price_list_uuid]) => {
        return this.contractManagementApi.get<contractmanagement_api.PriceListDetailResponse>('/management/v2/pricelists/' + price_list_uuid, {}).pipe(
          map(result => {
            return PriceListV2Actions.getPriceListSuccess({
              price_list_uuid: price_list_uuid,
              result
            });
          }),
          catchError(error => of(PriceListV2Actions.getPriceListFailure({ error, price_list_uuid: price_list_uuid }), globalFailure({ error })))
        );
      })
    )
  );

  createPriceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.createPriceList),
      concatMap(
        action =>
          this.contractManagementApi.post<contractmanagement_api.PriceListDetailResponse>('/management/v2/pricelists', action.data).pipe(
            map(result => {
              return PriceListV2Actions.createPriceListSuccess({
                price_list_uuid: result.data.id
              });
            }),
            catchError(error => of(PriceListV2Actions.createPriceListFailure({ error }), globalFailure({ error })))
          )
      )
    )
  );

  addContractsToPriceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.addAccountsToPriceList),
      concatMap(
        action =>
          this.contractManagementApi.patch<PriceListV2>('/management/v2/pricelists/' + action.price_list_uuid, { contracts: action.accounts }).pipe(
            map(result => {
              return PriceListV2Actions.addContractsToPriceListSuccess({
                price_list_uuid: action.price_list_uuid
              });
            }),
            catchError(error => of(PriceListV2Actions.addContractsToPriceListFailure({ error, price_list_uuid: action.price_list_uuid }), globalFailure({ error })))
          )
      )
    )
  );

  patchPriceListPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.patchPriceListPrices),
      concatMap(
        action =>
          this.contractManagementApi.patch<PriceListV2>('/management/v2/pricelists/' + action.price_list_uuid + '/locations/' + action.location_uuid, action.data).pipe(
            map(result => {
              return PriceListV2Actions.patchPriceListPricesSuccess({
                price_list_uuid: action.price_list_uuid
              });
            }),
            catchError(error => of(PriceListV2Actions.patchPriceListPricesFailure({ error, price_list_uuid: action.price_list_uuid }), globalFailure({ error })))
          )
      )
    )
  );

  deletePriceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.deletePriceList),
      concatMap(_action => {
        return this.contractManagementApi.delete<PriceListV2>('/management/v2/pricelists/' + _action.price_list_uuid).pipe(
          map(result =>
            PriceListV2Actions.deletePriceListSuccess({
              price_list_uuid: _action.price_list_uuid
            })
          ),
          catchError(error => {
            return of(PriceListV2Actions.deletePriceListFailure({ error, price_list_uuid: _action.price_list_uuid }), globalFailure({ error }));
          })
        )
      })
    )
  );

  patchPriceList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.patchPriceList),
      concatMap(
        action =>
          this.contractManagementApi.patch<PriceListV2>('/management/v2/pricelists/' + action.price_list_uuid, action.data).pipe(
            map(result => {
              return PriceListV2Actions.patchPriceListSuccess({
                price_list_uuid: action.price_list_uuid
              });
            }),
            catchError(error => of(PriceListV2Actions.patchPriceListFailure({ error, price_list_uuid: action.price_list_uuid }), globalFailure({ error })))
          )
      )
    )
  );

  getPriceListContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.getPriceListContracts, PriceListV2Actions.addContractsToPriceListSuccess),
      concatMap(
        action =>
          this.contractManagementApi.get<contractmanagement_api.PaginatedContracts>('/management/v2/pricelists/' + action.price_list_uuid + '/contracts', { 'page[number]': '1', 'page[size]': '20' }).pipe(
            map(result => {
              return PriceListV2Actions.getPriceListContractsSuccess({
                result,
                price_list_uuid: action.price_list_uuid
              });
            }),
            catchError(error => of(PriceListV2Actions.patchPriceListPricesFailure({ error, price_list_uuid: action.price_list_uuid }), globalFailure({ error })))
          )
      )
    )
  );
  getPriceListContractsNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PriceListV2Actions.getPriceListContractsNext),
      withLatestFrom(this.store.select(getSelectedPriceListContractsMeta)),
      concatMap(
        ([action, config]) =>
          this.contractManagementApi
            .get<contractmanagement_api.PaginatedContracts>('/management/v2/pricelists/' + action.price_list_uuid + '/contracts', { 'page[number]': (config.page * 1 + 1).toString(), 'page[size]': config.limit.toString() })
            .pipe(
              map(result => {
                return PriceListV2Actions.getPriceListContractsNextSuccess({
                  result,
                  price_list_uuid: action.price_list_uuid
                });
              }),
              catchError(error => of(PriceListV2Actions.getPriceListContractsNextFailure({ error, price_list_uuid: action.price_list_uuid }), globalFailure({ error })))
            )
      )
    )
  );
}
